import { WisdomSpan, SpanMessage, SpanMessageValue } from '../../../../api/api-client';
import { LogItemProps } from '../log-viewer/log-item';
import { formatTime } from '../../../utils/format';
import { getSpanIcon } from './get-span-icon';

/** Converts snake_case span name to Title Case */
function getSpanTitle(span: WisdomSpan): string {
  const name = span.attributes.operationName || span.spanName;
  return name
    .split('_')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Formats prompt type into human-readable stage name */
function formatPromptType(promptType: string): string {
  // Remove common prefixes and format to Title Case
  return promptType
    .replace('SELF_SERVICE_', '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c: string) => c.toUpperCase());
}

/** Extracts tool name from inputMessages for Execute Tool spans */
function extractToolName(span: WisdomSpan): string | undefined {
  const inputMessages = span.attributes.inputMessages;
  if (!inputMessages || inputMessages.length === 0) return undefined;

  // Check the first message's values for toolUse
  const firstMessage = inputMessages[0];
  if (!firstMessage.values || firstMessage.values.length === 0) return undefined;

  // Find the first toolUse in the values array
  for (const value of firstMessage.values) {
    if (value.toolUse?.name) {
      return value.toolUse.name;
    }
  }

  return undefined;
}

/** Creates subtitle with key span information */
function getSpanSubtitle(span: WisdomSpan): string {
  const a = span.attributes;

  // Execute Tool - show tool name if available
  if (a.operationName === 'execute_tool') {
    const toolName = extractToolName(span);
    if (toolName) {
      return `Tool: ${toolName}`;
    }
    // Fallback to toolName attribute if extraction fails
    if (a.toolName) {
      return `Tool: ${a.toolName}`;
    }
  }

  // Inference with prompt type - show the processing stage
  if (a.promptType) {
    return formatPromptType(a.promptType);
  }

  // Agent invocations - show agent name and type
  if (a.aiAgentName) {
    return `Agent: ${a.aiAgentName} (${a.aiAgentType || 'Unknown'})`;
  }

  // Tool execution - show tool name
  if (a.toolName) {
    return `Tool: ${a.toolName}`;
  }

  // Knowledge base retrieval - show KB ID
  if (a.knowledgeBaseId) {
    return `KB: ${a.knowledgeBaseId}`;
  }

  // Default fallback
  return `${span.spanType} span`;
}

/** Extracts citations from outputMessages and formats them for display */
function extractCitations(span: WisdomSpan): string | undefined {
  const messages = span.attributes.outputMessages || [];
  const allCitations = messages.flatMap((msg: SpanMessage) => {
    if (!msg.values) return [];
    return msg.values.flatMap((val: SpanMessageValue) => val?.text?.citations || []);
  });

  if (allCitations.length === 0) return undefined;

  // Get first citation title
  const firstTitle = allCitations[0]?.title;
  if (!firstTitle) return `${allCitations.length} citation(s)`;

  // Truncate title if too long
  const maxTitleLength = 50;
  const truncatedTitle =
    firstTitle.length > maxTitleLength ? firstTitle.substring(0, maxTitleLength) + '...' : firstTitle;

  // Show first title + count if multiple
  if (allCitations.length === 1) {
    return `KB: ${truncatedTitle}`;
  } else {
    return `KB: ${truncatedTitle} +${allCitations.length - 1} more`;
  }
}

/** Calculates the indent level (depth) of a span based on parent hierarchy */
function calculateIndentLevel(span: WisdomSpan, spanMap: Map<string, WisdomSpan>): number {
  let depth = 0;
  let currentSpan = span;

  // Traverse up the parent chain
  while (currentSpan.parentSpanId) {
    depth++;
    const parent = spanMap.get(currentSpan.parentSpanId);
    if (!parent) break; // Safety: stop if parent not found
    currentSpan = parent;
  }

  return depth;
}

/** Gets operation type category for styling */
function getOperationType(operationName = ''): string {
  const operation = operationName.toLowerCase();

  if (operation.includes('invoke_agent') || operation.includes('agent')) {
    return 'agent';
  }
  if (operation.includes('inference') || operation.includes('llm')) {
    return 'inference';
  }
  if (operation.includes('retrieve') || operation.includes('knowledge')) {
    return 'knowledge';
  }
  if (operation.includes('tool') || operation.includes('execution')) {
    return 'tool';
  }

  return 'default';
}

/** Transforms a single span to LogItemProps */
function transformSpanToLogItem(
  span: WisdomSpan,
  index: number,
  indentLevel: number,
  instanceAlias: string,
  accountId: string
): LogItemProps {
  const title = getSpanTitle(span);
  const subtitle = getSpanSubtitle(span);
  const citationInfo = extractCitations(span);

  // Append citation info to subtitle
  const fullSubtitle = citationInfo ? `${subtitle} | ${citationInfo}` : subtitle;

  return {
    timestamp: formatTime(span.startTimestamp),
    title,
    subtitle: fullSubtitle,
    parameters: {
      ...span.attributes,
      _operationType: getOperationType(span.attributes.operationName || span.spanName)
    },
    identifier: span.spanId,
    result: span.status,
    icon: getSpanIcon(span.spanName, span.attributes.operationName),
    indentLevel,
    grouped: false,
    raw: span as unknown,
    index,
    instanceAlias,
    accountId
  } as LogItemProps;
}

/** Transforms array of spans to LogItemProps, sorted chronologically with hierarchy indenting */
export function transformSpansToLogItems(
  spans: WisdomSpan[],
  instanceAlias: string,
  accountId: string
): LogItemProps[] {
  // Sort by timestamp (chronological order)
  const sorted = [...spans].sort((a, b) => new Date(a.startTimestamp).getTime() - new Date(b.startTimestamp).getTime());

  // Build span map for parent lookup
  const spanMap = new Map<string, WisdomSpan>();
  spans.forEach((span) => spanMap.set(span.spanId, span));

  // Calculate indent level for each span and transform
  return sorted.map((span, index) => {
    const indentLevel = calculateIndentLevel(span, spanMap);
    return transformSpanToLogItem(span, index, indentLevel, instanceAlias, accountId);
  });
}
